import { useState } from "react";
import { ControllerOptions, DataController } from "../types";
import { BaseDBEntity } from "@nt-portal/server-sdk";
import useStateRef from "react-usestateref";
import { createUUID } from "../common";
import { useShowMessage } from "./use-show-message";
import { MessageType } from "../redux/message-box.slice";




/**
 * Wraps a data service with standard state tracking and CRUD helpers.
 * @param service - Data controller implementation used to load and mutate entities.
 * @param logging - When true, writes basic lifecycle logs to the console.
 * @returns Tuple containing data, total count, busy flag, and an actions API.
 */
export function useDataController<T extends BaseDBEntity>(service: DataController<T>, logging?: boolean) {

    const showMessage = useShowMessage();
    const [_data, setData, _dataRef] = useStateRef<T[]>([])
    const [_total, setTotal] = useState<number>(0)
    const [_busy, setBusy] = useState<boolean>(false)
    const [_serviceParams, setServiceParams, _serviceParamsRef] = useStateRef<any>({});
    const [_block, setBlock] = useState<string>("");
    let _lastGetCallId: string = "";

    const log = (message: string, options: ControllerOptions<T>, type: "debug" | "error" = "debug") => {
        if (logging) {
            if (type === "error")
                console.error(message, options)
            else
                console.log(message, options)

        }
    }

    const actions = {

        clear() {
            setData([])
            setTotal(0)
        },

        startBlock() {
            let id = createUUID();
            setBlock(id);
            setBusy(true);
            return id;
        },

        endBlock(id: string) {
            if (id && id === _block) {
                setBlock("");
                setBusy(false);
            }
        },

        setParams(params: any) {
            setServiceParams(params)
        },

        getParams() {
            return _serviceParams;
        },

        sort(func: (a: T, b: T) => number) {
            let data = [..._dataRef.current]
            data.sort(func);
            setData(data);
        },


        init(inItem: Partial<T>) {
            if (service.init) {
                return service.init(inItem);
            }
        },

        updateItem(item: T) {
            let data = [..._dataRef.current]
            let index = data.findIndex(x => x.id === item?.id)
            if (index != -1) {
                data[index] = item;
            }
            else {
                data.push(item);
                let total = _total + 1;
                setTotal(total);
            }
            setData(data);

        },



        updateItems(items: T[]) {

            const data = [..._dataRef.current];
            // Create a lookup map once
            const idToIndexMap = new Map(data.map((item, index) => [item.id, index]));

            for (const element of items) {
                const index = idToIndexMap.get(element.id);
                if (index !== undefined) {
                    data[index] = element;
                }
            }
            setData(data);
        },

        async custom(procName: string, options: ControllerOptions<T>) {
            let thisService = service as any;
            if (typeof thisService[procName] === 'function') {
                thisService.params = _serviceParamsRef.current;
                setBusy(true)
                try {
                    return await thisService[procName](options);
                } finally {
                    setBusy(false)
                }
            }
        },

        async get(options: ControllerOptions<T>, postProcess?: (data: T[]) => T[]) {
            log("get", options)
            setBusy(true)
            const callId = createUUID()
            _lastGetCallId = callId;
            try {
                service.params = _serviceParamsRef.current;
                let result = await service.get(options);
                if (postProcess) {
                    result.data = postProcess(result.data);
                }
                if (callId === _lastGetCallId) {
                    if (result) {
                        setData(result.data);
                        setTotal(result.total)
                    } else {
                        setData([])
                        setTotal(0)
                    }
                }
                return result;
            }
            catch (e) {
                showMessage({ message: (e as Error).message, messageType: MessageType.Error, duration: 5000, position: "top-center" })


            } finally {
                setBusy(false)
            }

        },

        async getById(options: ControllerOptions<T>) {
            log("getById", options)
            setBusy(true)
            try {
                if (service.getById) {
                    service.params = _serviceParamsRef.current;
                    if (options.ids) {
                        let innerOptions = { ...options }
                        let innerData = new Array<T>();
                        for (let i = 0; i < options.ids.length; i++) {
                            const id = options.ids[i];
                            innerOptions.id = id;
                            let item = await service.getById(innerOptions);
                            if (item) {
                                innerData.push(item)
                            }
                        }
                        let data = [..._dataRef.current]
                        for (let i = 0; i < innerData.length; i++) {
                            const item = innerData[i];
                            let index = data.findIndex(x => x.id === item?.id)
                            if (index !== -1)
                                data[index] = item;
                            else
                                data.push(item)
                        }
                        setData(data)
                    } else {
                        let item = await service.getById(options);
                        if (item) {
                            let index = _data.findIndex(x => x.id === item?.id)
                            let data = [..._dataRef.current]
                            if (index !== -1)
                                data[index] = item;
                            else
                                data.push(item)
                            setData(data)
                        }
                        return item;
                    }
                }
            }
            catch (e) {
                showMessage({ message: (e as Error).message, messageType: MessageType.Error, duration: 5000, position: "top-center" })

            } finally {
                setBusy(false)
            }

        },




        async getLookup(options: ControllerOptions<T>) {
            log("getLookUp", options)
            setBusy(true)
            try {
                if (service.getLookup) {
                    service.params = _serviceParamsRef.current;
                    let result = await service.getLookup(options)
                    if (result) {
                        setData(result.data);
                        setTotal(result.total)
                    } else {
                        setData([])
                        setTotal(0)
                    }
                    return result;
                } else {
                    throw new Error("getLookup not defined on service.")
                }
            }
            catch (e) {
                showMessage({ message: (e as Error).message, messageType: MessageType.Error, duration: 5000, position: "top-center" })

            } finally {
                setBusy(false)

            }
        },

        async update(options: ControllerOptions<T>, postProcess?: (data: T[]) => T[]) {
            log("update", options)
            if (!_block) setBusy(true)
            try {
                if (service.update && options.item) {
                    service.params = _serviceParamsRef.current;
                    let result = await service.update(options) as T
                    options.id = options.item.id;
                    if (service.getById) {
                        let item = await service.getById(options)
                        if (item) {
                            let data = [..._dataRef.current]
                            let index = data.findIndex(x => x.id === options.id)
                            if (index !== -1)
                                data[index] = item;
                            else
                                data.push(item)
                            if (postProcess) {
                                data = postProcess(data);
                            }
                            setData(data);
                        }
                        return item;
                    } else if (result) {
                        let data = [..._dataRef.current]
                        let index = data.findIndex(x => x.id === result.id)
                        if (index !== -1)
                            data[index] = result;
                        else
                            data.push(result)
                        if (postProcess) {
                            data = postProcess(data);
                        }
                        setData(data);
                        return result;
                    }
                } else {
                    log("No update or item", options, "error")
                }
            }
            catch (e) {
                showMessage({ message: (e as Error).message, messageType: MessageType.Error, duration: 5000, position: "top-center" })

            } finally {
                if (!_block) setBusy(false)
            }

        },
        async delete(options: ControllerOptions<T>) {
            log("delete", options)
            if (!_block) setBusy(true)
            try {
                if (service.delete) {
                    service.params = _serviceParamsRef.current;
                    await service.delete(options)
                    return await this.get(options)
                }
                return null;
            }
            catch (e) {
                showMessage({ message: (e as Error).message, messageType: MessageType.Error, duration: 5000, position: "top-center" })

            } finally {
                if (!_block) setBusy(false)
            }
        },
        async add(options: ControllerOptions<T>) {
            log("add", options)
            if (!_block) setBusy(true)
            try {
                if (service.add) {
                    service.params = _serviceParamsRef.current;
                    let item = await service.add(options)
                    this.get(options);

                    return item;
                }
            }
            catch (e) {
                showMessage({ message: (e as Error).message, messageType: MessageType.Error, duration: 5000, position: "top-center" })

            } finally {
                if (!_block) setBusy(false)
            }
        },

        async clone(options: ControllerOptions<T>) {
            log("clone", options)
            setBusy(true)
            try {
                if (service.clone) {
                    service.params = _serviceParamsRef.current;
                    let item = await service.clone(options)
                    this.get(options);

                    return item;
                }
            }
            catch (e) {
                showMessage({ message: (e as Error).message, messageType: MessageType.Error, duration: 5000, position: "top-center" })

            } finally {
                setBusy(false)
            }
        },

        async inUse(options: ControllerOptions<T>) {
            log("inUse", options)
            if (service.inUse) {
                setBusy(true)
                try {
                    service.params = _serviceParamsRef.current;
                    return await service.inUse(options)

                } catch (e) {
                    showMessage({ message: (e as Error).message, messageType: MessageType.Error, duration: 5000, position: "top-center" })

                } finally {
                    setBusy(false)
                }
            }
        },


        async import(options: ControllerOptions<T>) {
            log("import", options)
            if (service.import) {

                setBusy(true)
                try {
                    service.params = _serviceParamsRef.current;
                    let item = await service.import(options)
                    return item;
                }
                catch (e) {
                    showMessage({ message: (e as Error).message, messageType: MessageType.Error, duration: 5000, position: "top-center" })

                } finally {
                    setBusy(false)
                }
            } else {
                throw new Error("Service does not support Import.")
            }
        },


        async export(options: ControllerOptions<T>) {
            log("export", options)
            try {
                if (service.export) {
                    service.params = _serviceParamsRef.current;
                    let item = await service.export(options)
                    return item;
                }
                throw new Error("Service does not support Export.")
            }
            catch (e) {
                showMessage({ message: (e as Error).message, messageType: MessageType.Error, duration: 5000, position: "top-center" })

            }

        }
    }



    return [_data, _total, _busy, actions] as const;

}
